import { useState } from 'react';
import { Link } from 'react-router-dom';

import { type CrawlRun, isRunActive } from '@/hooks/useCrawlRuns';
import { clockTime, durationSec, relTime, shortError } from '@/lib/crawlRuns';

import { ChevronDownIcon, CheckCircleIcon, CronIcon, HandIcon, RefreshIcon, XCircleIcon } from './crawlIcons';

const GREEN = '#3f7d4e';
const WARNINGS_PREVIEW = 3;

function TriggerBadge({ trigger }: { trigger: string }) {
  const manual = trigger === 'manual';
  return (
    <span
      className={
        manual
          ? 'inline-flex items-center gap-1 rounded-md bg-wolisho-brown/[0.09] px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.03em] text-wolisho-brown'
          : 'inline-flex items-center gap-1 rounded-md border border-wolisho-brown/[0.14] px-[7px] py-0.5 text-[10.5px] font-bold uppercase tracking-[0.03em] text-wolisho-brown/40'
      }
    >
      {manual ? <HandIcon size={10} /> : <CronIcon size={10} />}
      {manual ? 'Manual' : 'Cron'}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  // Queued is the gap between the endpoint accepting a crawl and a consumer
  // picking it up — named apart from Running so a stuck queue is visible.
  if (status === 'queued') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-wolisho-brown/55">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-wolisho-brown/45" />
        Queued
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-wolisho-brown">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-wolisho-brown border-t-transparent" />
        Running…
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-wolisho-red">
        <XCircleIcon size={13} /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: GREEN }}>
      <CheckCircleIcon size={13} /> OK
    </span>
  );
}

// Compact listings delta on OK runs: +created ~changed −delisted.
function ListingDelta({ run }: { run: CrawlRun }) {
  const parts: { text: string; color: string; title: string }[] = [];
  if (run.listingsCreated)
    parts.push({ text: `+${String(run.listingsCreated)}`, color: GREEN, title: 'created' });
  if (run.listingsChanged)
    parts.push({ text: `~${String(run.listingsChanged)}`, color: 'var(--wolisho-brown, #361100)', title: 'changed' });
  if (run.listingsDelisted)
    parts.push({ text: `−${String(run.listingsDelisted)}`, color: 'rgba(54,17,0,0.5)', title: 'delisted' });
  if (parts.length === 0) {
    return <span className="text-xs font-bold text-black/30">no changes</span>;
  }
  return (
    <span className="inline-flex gap-2 tabular-nums">
      {parts.map((part) => (
        <span key={part.title} title={part.title} className="text-[12.5px] font-bold" style={{ color: part.color }}>
          {part.text}
        </span>
      ))}
    </span>
  );
}

// How far the pipeline has got with what this run handed it: green_coffee rows
// over listings queued. Absent on runs staged before the counters existed.
function RunProgress({ run }: { run: CrawlRun }) {
  if (run.listingsQueued === null) return null;
  const done = run.listingsDone ?? 0;
  return (
    <span
      className="text-[12.5px] font-bold tabular-nums text-wolisho-brown/60"
      title="listings normalized"
      data-testid={`crawl-run-progress-${run.id}`}
    >
      {done}/{run.listingsQueued}
    </span>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="rounded-lg bg-wolisho-brown/[0.04] px-2.5 py-2">
      <div className="text-base font-bold leading-none tabular-nums" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
      <div className="mt-[3px] text-[10.5px] font-bold text-black/40">{label}</div>
    </div>
  );
}

function WarningsBlock({ warnings }: { warnings: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? warnings : warnings.slice(0, WARNINGS_PREVIEW);
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-wolisho-brown">
          {warnings.length} classifier warning{warnings.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {visible.map((warning, index) => (
          <li
            key={`${String(index)}-${warning}`}
            className="relative pl-3 text-xs leading-snug text-wolisho-brown/75"
          >
            <span className="absolute left-0 text-wolisho-brown/30">·</span>
            {warning}
          </li>
        ))}
      </ul>
      {warnings.length > WARNINGS_PREVIEW && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="mt-1.5 border-0 bg-transparent p-0 text-xs font-bold text-wolisho-red"
        >
          {expanded ? 'Show less' : `Show all ${String(warnings.length)}`}
        </button>
      )}
    </div>
  );
}

interface CrawlRunRowProps {
  run: CrawlRun;
  defaultOpen?: boolean;
  highlight?: boolean;
  onRerun: (run: CrawlRun) => void;
}

export function CrawlRunRow({ run, defaultOpen, highlight, onRerun }: CrawlRunRowProps) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  // Queued and running are both "no verdict yet": nothing to expand, and the
  // start time reads better as a clock than as an age.
  const active = isRunActive(run);
  const failed = run.status === 'failed';
  const duration = durationSec(run.startedAt, run.finishedAt);

  return (
    <div
      className={`px-4 py-[13px] transition-colors ${highlight ? 'bg-wolisho-red/[0.05]' : ''}`}
      data-testid={`crawl-run-${run.id}`}
    >
      <button
        type="button"
        disabled={active}
        aria-expanded={active ? undefined : open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2.5 border-0 bg-transparent p-0 text-left disabled:cursor-default"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[14.5px] font-bold text-black">{run.roasterName}</span>
            <TriggerBadge trigger={run.trigger} />
          </div>
          <div className="mt-1 flex items-center gap-[7px]">
            <StatusPill status={run.status} />
            {!active && (
              <>
                <span className="text-black/[0.18]">·</span>
                {failed ? (
                  <span className="max-w-[150px] truncate text-xs font-bold text-wolisho-red">
                    {shortError(run.error)}
                  </span>
                ) : (
                  <ListingDelta run={run} />
                )}
              </>
            )}
            {/* Shown on terminal runs too: the feed stage finishing only means
                the listings are queued, not that the pipeline is through them. */}
            {run.listingsQueued !== null && (
              <>
                <span className="text-black/[0.18]">·</span>
                <RunProgress run={run} />
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11.5px] font-bold tabular-nums text-black/40">
            {active ? clockTime(run.startedAt) : relTime(run.startedAt)}
          </span>
          {!active && (
            <span className={`flex text-wolisho-brown/35 transition-transform ${open ? 'rotate-180' : ''}`}>
              <ChevronDownIcon size={16} />
            </span>
          )}
        </div>
      </button>

      {open && !active && (
        <div className="mt-3 border-t border-wolisho-brown/[0.07] pt-3">
          <div
            className={`grid grid-cols-3 gap-2 ${failed || run.warnings.length > 0 ? 'mb-3' : ''}`}
          >
            <Stat label="Pages" value={run.pagesFetched} />
            <Stat label="Products seen" value={run.productsSeen} />
            <Stat label="Duration" value={duration ? `${String(duration)}s` : '—'} />
            <Stat label="Created" value={run.listingsCreated} accent={GREEN} />
            <Stat label="Changed" value={run.listingsChanged} accent="var(--wolisho-brown, #361100)" />
            <Stat label="Delisted" value={run.listingsDelisted} accent="rgba(54,17,0,0.5)" />
          </div>

          {failed && (
            <div className={run.warnings.length > 0 ? 'mb-3' : ''}>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-wolisho-red">
                  Error
                </span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRerun(run);
                  }}
                  data-testid={`crawl-rerun-${run.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border-0 bg-wolisho-red px-[11px] py-[5px] text-xs font-bold text-white"
                >
                  <RefreshIcon size={12} /> Re-run
                </button>
              </div>
              <code className="block whitespace-pre-wrap break-words rounded-lg bg-wolisho-red/[0.07] px-[11px] py-[9px] font-mono text-xs leading-normal text-wolisho-brown">
                {run.error}
              </code>
            </div>
          )}

          {run.warnings.length > 0 && <WarningsBlock warnings={run.warnings} />}

          {!failed && run.listingsCreated + run.listingsChanged > 0 && (
            <div
              className={
                run.warnings.length > 0 ? 'mt-3 border-t border-wolisho-brown/[0.07] pt-3' : ''
              }
            >
              {/* No count: this run's created+changed delta is per-run, but the
                  review page shows the roaster-wide pending set, so a number here
                  would disagree with the page it links to. */}
              <Link
                to={`/admin/roasters/${run.roasterId}/review`}
                data-testid={`crawl-run-review-${run.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-wolisho-brown/[0.09] px-[13px] py-[7px] text-[12.5px] font-bold text-wolisho-brown"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-wolisho-brown" />
                Review staged listings
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
